import React from "react";
import { motion } from "framer-motion";
import { MaskedLine, FadeUp } from "./MaskedReveal";

const reasons = [
  {
    n: "01",
    title: "Insurance-Approved Training",
    body:
      "Our in-aircraft C408 SkyCourier initial and recurrent programs are approved through W. Brown & Associates — the leading aviation insurance underwriter for King Air and turbine aircraft operators. Your policy stays in force.",
    highlight: true,
  },
  {
    n: "02",
    title: "In-Aircraft, Not Only Sim",
    body:
      "We train in the real airplane. Sim time has its place, but muscle memory, energy management, and single-pilot workload are best learned where they'll be flown — behind the actual power levers.",
  },
  {
    n: "03",
    title: "Working Captains as Instructors",
    body:
      "Every instructor still flies the type in real operations — cargo, corporate, ferry, and government missions. No career-classroom theory. You learn from pilots who fly it Tuesday morning in real weather.",
  },
  {
    n: "04",
    title: "Small Cohorts, Personal Debriefs",
    body:
      "We deliberately cap enrollment. Every student gets a written debrief and a follow-up call. This is not a factory training line — it's mentorship at scale of one.",
  },
  {
    n: "05",
    title: "Worldwide Reach",
    body:
      "From training seats in North America to transatlantic and Pacific ferry deliveries, our footprint follows the aircraft. If your mission crosses oceans, so do we.",
  },
  {
    n: "06",
    title: "Kingdom Aviation Ethos",
    body:
      "We fly with reverence. Discipline over drama. The airplane, the mission, the standard — in that order. It's an ethic our clients feel from the first phone call.",
  },
];

export const WhyChoose = () => {
  return (
    <section
      id="why"
      className="relative bg-[#050505] py-24 md:py-40 border-t border-white/10 overflow-hidden"
      data-testid="why-choose-section"
    >
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 0.03 }}
        viewport={{ once: true, amount: 0 }}
        transition={{ duration: 2 }}
        className="pointer-events-none absolute left-[-6%] bottom-[-10%] font-display text-white text-[24rem] md:text-[36rem] leading-none tracking-tighter select-none"
        aria-hidden
      >
        WHY
      </motion.div>

      <div className="relative mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24">
          <div className="md:col-span-6">
            <FadeUp>
              <div className="font-mono-label text-white/50 mb-6">
                <span className="text-[#FF5A00]">◉</span> Why · El Elyon Air
              </div>
            </FadeUp>
            <h2 className="font-display text-5xl md:text-7xl lg:text-8xl uppercase leading-[0.86] tracking-tight">
              <MaskedLine>Why choose</MaskedLine>
              <MaskedLine delay={0.12}>
                <span className="font-serif-italic italic text-white/70">
                  El Elyon Air.
                </span>
              </MaskedLine>
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex items-end">
            <FadeUp delay={0.2}>
              <p className="text-white/60 text-base md:text-lg leading-relaxed max-w-md">
                Six reasons owners, captains, and agencies pick us — and, more
                importantly, stay with us for their recurrent cycles.
              </p>
            </FadeUp>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {reasons.map((r, i) => (
            <motion.div
              key={r.n}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0, margin: "0px 0px -10% 0px" }}
              transition={{
                duration: 1,
                ease: [0.76, 0, 0.24, 1],
                delay: (i % 3) * 0.08,
              }}
              className={`relative border p-6 md:p-8 flex flex-col justify-between min-h-[280px] ${
                r.highlight
                  ? "border-[#FF5A00]/60 bg-[#FF5A00]/[0.04]"
                  : "border-white/10 bg-[#0A0A0C]"
              }`}
              data-testid={`why-reason-${r.n}`}
            >
              <div className="flex items-start justify-between">
                <span className="font-serif-italic text-white/40 text-4xl md:text-5xl leading-none">
                  {r.n}
                </span>
                {r.highlight && (
                  <span className="font-mono-label text-[#FF5A00]">
                    ★ Insured
                  </span>
                )}
              </div>
              <div className="mt-8">
                <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight leading-tight mb-4">
                  {r.title}
                </h3>
                <p className="text-white/65 text-sm md:text-[15px] leading-relaxed">
                  {r.body}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
